import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

export interface UseInvocationQueueStatsOptions {
  /** Pass with `actionOrgId` to get one action's median runtime rather than the org-wide blend. */
  actionName?: string;
  actionOrgId?: string;
  /** Milliseconds between refetches, defaulting to 10 000; `false` fetches once. */
  refetchInterval?: number | false;
}

/**
 * How deep the current org's invocation queue is, and how long it is likely to take to drain.
 */
export function useInvocationQueueStats(
  options?: UseInvocationQueueStatsOptions,
) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const actionName = options?.actionName;
  const actionOrgId = options?.actionOrgId;

  return useQuery({
    queryKey: [
      "invocationQueueStats",
      currentOrganization?.org_id,
      actionName,
      actionOrgId,
    ],
    queryFn: async ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }

      return actionService.getInvocationQueueStats(currentOrganization.org_id, {
        abortSignal: signal,
        actionName,
        actionOrgId,
      });
    },
    enabled: currentOrganization !== null,
    refetchInterval: options?.refetchInterval ?? 10_000, // ms
    staleTime: 0,
  });
}
